import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import type { TViewerPane } from "services/annotations.service"

// One entry per live viewport that canvas handlers must be bound to (REQ-399 split screen).
export type TPaneBinding = { pane: TViewerPane; documentViewer: Core.DocumentViewer; scrollEl: HTMLElement }

// The live viewer panes: one in single-viewer mode, two while split screen is open. Resolve this
// fresh on every use — Apryse replaces the scroll elements when it re-mounts the document area, so
// nothing here may be cached across a `layoutEpoch` bump.
export const resolveViewerPanes = (instance: WebViewerInstance | null): TPaneBinding[] => {
	if (!instance) return []
	const viewers = instance.Core.getDocumentViewers?.() ?? [instance.Core.documentViewer]
	const panes: TPaneBinding[] = []
	viewers.forEach((documentViewer, index) => {
		if (!documentViewer) return
		const scrollEl = documentViewer.getScrollViewElement?.() as HTMLElement | null
		if (!scrollEl?.isConnected) return
		// The secondary viewer exists before it holds a document; skip it until it does.
		if (index > 0 && !documentViewer.getDocument?.()) return
		panes.push({ pane: index === 0 ? "primary" : "split", documentViewer, scrollEl })
	})
	return panes
}

// How long to wait for a pane re-layout to settle before the final viewport restore. Matches
// useSplitScreen's own LAYOUT_SETTLE_MS — it is the same animation being waited out.
const PANE_LAYOUT_SETTLE_MS = 350

// Hold one pane's viewport still across a re-layout, and return a function that releases it.
//
// Why this is needed at all: the panes sit in a FIT zoom mode, so anything that changes a pane's
// WIDTH makes Apryse re-fit it — and a lower zoom at the same scroll offset also changes which page
// the pane is showing. Opening a sidebar is the common trigger. Measured on a 1440 window: opening
// the second sidebar took the pane the user was reading from page 14 at 22% to page 27 at 12%.
//
// Smart navigation (#1311 D12 / AC18) must not move the PRIMARY pane — the one the user just clicked
// in and is looking at — yet it necessarily opens the SECONDARY pane's sidebar, which narrows both.
//
// It PREVENTS the re-fit rather than undoing it. The earlier version snapshotted zoom + scroll and
// put them back afterwards, which worked but was visible: restoring by `zoomTo` repaints the pane,
// so the user saw the pane they were reading re-render while the other one navigated. Pinning the
// fit mode to `Zoom` freezes the numeric zoom for the duration, so a width change no longer re-fits
// the pane and there is nothing to undo. The previous fit mode is put back on release, and the
// scroll offsets are corrected only if something moved them anyway — a belt, not the mechanism.
//
// `documentViewer.setFitMode`, never `UI.setFitMode`: the UI helpers default to viewer 1, so they
// would silently retarget the wrong pane (see AGENTS.md, "Tools and the split screen").
//
// Caller contract: only call this for a pane that is NOT itself the target of an in-flight
// `frameAnnotations` call right now. The snapshot is taken from wherever the pane happens to be at
// call time, and the correction 350ms later can't tell "unwanted drift from a width change" apart
// from "a centering call that finished exactly on schedule" — it will revert the latter right back
// to its pre-call position (PR #1347 review: this reverted a correctly-centered eref click every
// time smart navigation was on, because the click's own centering was mistaken for drift).
// `frameAnnotations`'s own settle loop already re-measures panel insets on every pass, so a pane
// that's actively centering needs no help staying correct through a width change.
export const preservePaneViewport = (instance: WebViewerInstance | null, pane: TViewerPane): (() => void) => {
	const binding = resolveViewerPanes(instance).find((candidate) => candidate.pane === pane)
	if (!binding) return () => {}
	const { documentViewer, scrollEl } = binding
	const zoom = documentViewer.getZoomLevel?.()
	const { scrollLeft, scrollTop } = scrollEl

	// Freeze the zoom now, BEFORE the caller opens anything: FitMode.Zoom means "hold the numeric
	// zoom", so the pane stops re-fitting itself when its width changes.
	const zoomFitMode = documentViewer.FitMode?.Zoom
	if (zoomFitMode && documentViewer.getFitMode?.() !== zoomFitMode) documentViewer.setFitMode(zoomFitMode)

	const release = () => {
		if (!scrollEl.isConnected) return
		// Scroll offsets keep their meaning while the zoom is pinned, so a difference here is either
		// the user's own scroll or a stray write. Only correct an exact mismatch, and never re-zoom:
		// a `zoomTo` is what made this visible in the first place.
		if (scrollEl.scrollLeft !== scrollLeft) scrollEl.scrollLeft = scrollLeft
		if (scrollEl.scrollTop !== scrollTop) scrollEl.scrollTop = scrollTop
	}

	// The pane STAYS pinned to FitMode.Zoom afterwards, deliberately. Putting its previous fit mode
	// back would re-fit it at the new, narrower width — which is the very jump this exists to
	// prevent, just moved 350ms later. Holding the zoom the user was reading at is what D12 asks for;
	// the pane picks a fit mode up again the next time the user zooms or fits it themselves.
	const correctZoom = () => {
		if (!scrollEl.isConnected) return
		if (typeof zoom !== "number" || zoom <= 0) return
		if (documentViewer.getZoomLevel?.() === zoom) return
		documentViewer.zoomTo(zoom)
	}

	return () => {
		requestAnimationFrame(release)
		window.setTimeout(() => {
			correctZoom()
			release()
		}, PANE_LAYOUT_SETTLE_MS)
	}
}
